import React, { useState } from "react"
import { Button, Col, Dropdown, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import { CheckCircleFill, ChevronDown, ChevronUp, InfoCircleFill } from "react-bootstrap-icons"
import { ColumnDescription } from "react-bootstrap-table-next"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import shortId from "shortid"
import api from "../../api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import RemotePagination from "../../components/Tables/RemotePagination"
import { RootState } from "../../store"
import { editBag, editPallet, exportList } from "../../store/linehaul.store"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"
import ExportManifest from "./ExportManifest"
import LineHaulBagsTableExpandedRow from "./LineHaulBagsTableExpandedRow"
import LineHaulMAWBSTableExpandedRow from "./LineHaulMAWBSTableExpandedRow"
import LineHaulPalletsTableExpandedRow from "./LineHaulPalletsTableExpandedRow"

function LineHaulTable({
    data,
    setSelectedRows,
    selectedRows,
    setShow,
    tableName,
    options,
    setOptions,
    recordDetails = {},
    setRecordDetails = () => {},
    createMAWBModel = false,
    showMessage = () => {},
    setReload = () => {},
    reload = false,
    showPalletDetails = () => {},
    deleteDocument = () => {},
    setMawbAttachmentUpload
}: {
    data: Record<string, any>[]
    setSelectedRows: Function
    selectedRows: number[]
    setShow: Function
    tableName: string
    options: Record<string, any>
    setOptions: Function
    recordDetails?: Record<string, any>
    setRecordDetails?: Function
    createMAWBModel?: boolean
    showMessage?: Function
    setReload?: Function
    reload?: boolean
    showPalletDetails?: (pallet: any) => void
    deleteDocument?: (document: any) => void
    setMawbAttachmentUpload?: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const printMenifest = () => {
        // handleBtnClick()
    }
    const { country, success } = useAppSelector((state: RootState) => state.linehaul)

    const [manifestData, setManifestData] = useState({} as Record<string, any>)
    const { userInfo } = useAppSelector((state: RootState) => state.auth)
    const loading = useAppSelector((state: RootState) => state.linehaul.loading)
    const { selectedWarehouse } = useAppSelector((state: RootState) => state.warehouses)
    const defaultWarehouse = userInfo?.ParcelHubUserWarehouse?.find(
        (uw: Record<string, any>) => uw?.is_default
    )?.warehouse_id
    const exporting = useAppSelector((state: RootState) => state.linehaul.exporting)
    const statuses = useAppSelector((state: RootState) => state.linehaul.statuses)
    const statusesLoading = useAppSelector((state: RootState) => state.linehaul.statusesLoading)
    const tableId = tableName === "Pallets" ? "pallets" : tableName === "MAWBs" ? "mawbs" : "bags"
    const [exportParams, setExportParams] = useState({
        keyword: options.keyword[tableId],
        from: options.from[tableId],
        to: options.to[tableId],
        status: options.status[tableId]
    })

    const handleTableChange = (type: any, { page, sizePerPage }: { page: number; sizePerPage: number }) => {
        setOptions({
            ...options,
            page: {
                ...(options.page ?? {}),
                [tableId]: page
            },
            sizePerPage
        })
    }

    const exportManifest = async (
        row: Record<string, any>,
        category: "mawb" | "pallet" | "bag",
        uid: string
    ) => {
        try {
            let res = await api.linehaul.exportManifest({
                category,
                uid,
                warehouse: row?.warehouse_id?.toString(),
                country
            })
            if (res?.data?.status) {
                setManifestData({ ...res?.data?.payload })
                // setShow("")
                // dispatch(resetSuccess(true))
            } else {
                throw new Error(res?.data?.message)
            }
        } catch (error: any) {
            let err = error?.message ?? error
            console.log(err)
            showMessage(`Data export, reason: ${err}`, "danger", "Error")
        }
    }

    const handleAction = (action: string, row: Record<string, any>) => {
        setRecordDetails({ ...row })
        switch (tableName) {
            case "Pallets": {
                switch (action) {
                    case "Edit": {
                        dispatch(editPallet(row))
                        setShow("updatePallet")
                        return
                    }
                    case "Create": {
                        setShow("createPallet")
                        return
                    }
                    case "Delete": {
                        dispatch(editPallet(row))
                        setShow("deletePallet")
                        return
                    }
                    case "Print Label": {
                        const lhURL = row.pallet_id
                            ? `${process.env.REACT_APP_BASE_URL_PARCELS}/barcodes/${row.pallet_id}.pdf`
                            : ""
                        if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                        return
                    }
                    default:
                        return
                }
            }
            case "MAWBs": {
                switch (action) {
                    case "Edit": {
                        setShow("updateMAWB")
                        return
                    }
                    case "Create": {
                        setShow("createMAWB")
                        return
                    }
                    case "Update Status": {
                        setShow("updateMAWBStatus")
                        return
                    }
                    case "Export Manifest": {
                        setRecordDetails({})
                        exportManifest(row, "mawb", row.mawb)
                        return
                    }
                    case "Upload": {
                        setShow("documentUpload")
                        setMawbAttachmentUpload?.(row)
                        return
                    }
                    default:
                        return
                }
            }
            case "Bags": {
                switch (action) {
                    case "Create": {
                        setShow("createBag")
                        return
                    }
                    case "Edit": {
                        dispatch(editBag(row))
                        setShow("updateBag")
                        return
                    }
                    case "Delete": {
                        dispatch(editBag(row))
                        setShow("deleteBag")
                        return
                    }
                    case "Print Label": {
                        console.log(row)
                        const lhURL = row.bag_id
                            ? `${process.env.REACT_APP_BASE_URL_PARCELS}/barcodes/${row.bag_id}.pdf`
                            : ""
                        if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                        return
                    }
                    default:
                        return
                }
            }
            default: {
                return
            }
        }
    }

    const columns: Record<string, ColumnDescription<any, any>[]> = {
        Pallets: [
            {
                text: t("Pallet ID"),
                dataField: "pallet_id",
                formatter: (cell: string, row: Record<string, any>) => (
                    <span className="highlighted-text">{cell}</span>
                )
            },
            {
                text: t("No Of Bags"),
                dataField: "LHBags",
                formatter: (cell: any, row: Record<string, any>) => {
                    return `${cell?.length ?? 0}`
                }
            },
            {
                text: t("Dimensions"),
                dataField: "dims",
                formatter: (cell: any, row: Record<string, any>) =>
                    `${
                        row?.length || "-"
                    } x ${row?.width || "-"} x ${row?.height || "-"} ${row?.dimensions_unit || "inch|cm"}`
            },
            {
                text: t("A. Weight"),
                dataField: "a-weight",
                formatter: (cell: any, row: Record<string, any>) => {
                    return `${row?.actual_weight_value || "-"} ${row?.weight_unit || "lb|kg"}`
                }
            },
            {
                text: t("Ch. Weight"),
                dataField: "c-weight",
                formatter: (cell: any, row: Record<string, any>) => {
                    return `${row?.ch_weight_value || "-"} ${row?.weight_unit || "lb|kg"}`
                }
            },
            {
                text: t("Destination"),
                dataField: "destination",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id={`lh-${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>
                            }>
                            <>
                                <Flag
                                    className="d-inline-block mx-1"
                                    style={{ width: 28, height: 18 }}
                                    code={(cell ?? "").toLowerCase()}
                                />
                                &nbsp;{cell}
                            </>
                        </OverlayTrigger>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Value"),
                dataField: "pallet_invoice_value",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell && cell > 0 ? `$${cell}` : "-"
                }
            },
            {
                text: "",
                dataField: "actions",
                csvExport: false,
                hidden: createMAWBModel,
                formatter: (cell: number, row: Record<string, any>) => {
                    let actions = ["Print Label", "Edit", "Delete"]
                    return (
                        <Dropdown className="actions-menu">
                            <Dropdown.Toggle id="dropdown-basic" size={"sm"} className="py-0 secondary-bg">
                                {t("Actions")}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {actions?.map((action: string) => (
                                    <Dropdown.Item
                                        key={action}
                                        className="font-13"
                                        onClick={() => handleAction(action, row)}>
                                        {action}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )
                }
            }
        ],
        MAWBs: [
            {
                text: "",
                dataField: "mawbInfo",
                formatter: (cell: string, row: Record<string, any>) => {
                    let needsAttention = !row?.status_id || !row?.no_of_pallets || row?.no_of_pallets === 0
                    return (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id={`lh-${shortId.generate()}-tooltip`}>
                                    {t(`${needsAttention ? "Something is missing" : "Good job!"}`)}
                                </Tooltip>
                            }>
                            {needsAttention ? (
                                <InfoCircleFill className="text-danger" />
                            ) : (
                                <CheckCircleFill className="text-success" />
                            )}
                        </OverlayTrigger>
                    )
                }
            },
            {
                text: t("Master"),
                dataField: "mawb",
                formatter: (cell: string, row: Record<string, any>) => {
                    return !row?.status_id || !row?.no_of_pallets || row?.no_of_pallets === 0 ? (
                        <strong className="text-danger">{cell}</strong>
                    ) : (
                        <span className="highlighted-text">{cell}</span>
                    )
                }
            },
            {
                text: t("MAWB Date"),
                dataField: "created_at",
                formatter: (cell: string, row: Record<string, any>) => {
                    let dateAndTime, date, time
                    if (typeof cell === "string") {
                        dateAndTime = dateFormatter(cell, true, country)?.split(",")
                        date = dateAndTime[0]?.trim() ?? ""
                        time = dateAndTime[1]?.trim() ?? ""
                    }
                    return cell && cell?.length > 0 ? (
                        <>
                            <span className="d-block">{date}</span>
                            <small className="text-muted">{time}</small>
                        </>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Status"),
                dataField: "ParcelStatus",
                formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                    let statusId = options.status[tableId] ? parseInt(options.status[tableId]) : row?.status_id
                    let status =
                        row?.LHMawbStatusLogs?.length > 0
                            ? (row?.LHMawbStatusLogs?.filter(
                                  (l: Record<string, any>) => l.status_id === statusId
                              )
                                  ?.map((l: Record<string, any>) => ({
                                      name: l.ParcelStatus?.name,
                                      created_at: l.created_at,
                                      status_id: l.status_id
                                  }))
                                  ?.sort(
                                      (a: Record<string, any>, b: Record<string, any>) =>
                                          new Date(b.created_at)?.getTime() - new Date(a.created_at)?.getTime()
                                  ))[0]
                            : { ...cell }

                    let nowDate = new Date()
                    let departureDate = new Date(row?.etd)
                    let checkDeparture = true
                    if (
                        nowDate >= departureDate &&
                        !row?.LHMawbStatusLogs?.find((l: Record<string, any>) => l.status_id === 34)
                    ) {
                        checkDeparture = false
                    }
                    const statusIndicatorClassName =
                        status?.name === "Shipment handed over to last mile" ? "delivered" : "inprocess-status"
                    return status?.name && (checkDeparture || options.status[tableId]) ? (
                        <div className={statusIndicatorClassName}>{t(status?.name)}</div>
                    ) : row?.no_of_pallets && row?.no_of_pallets > 0 ? (
                        <strong className="text-danger mawb-notification">
                            {t("MAWB status update is required")}
                        </strong>
                    ) : (
                        <strong className="text-danger mawb-notification">{t("Add Pallets to MAWB")}</strong>
                    )
                }
            },
            {
                text: t("Status Date"),
                dataField: "LHMawbStatusLogs",
                formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                    let statusId = options.status[tableId] ? parseInt(options.status[tableId]) : row?.status_id
                    let logs = [...cell.filter((c) => c?.status_id === statusId)]

                    let dateAndTime, date, time
                    if (cell && cell?.length > 0) {
                        logs = logs?.sort(
                            (a: Record<string, any>, b: Record<string, any>) =>
                                new Date(b.created_at)?.getTime() - new Date(a.created_at)?.getTime()
                        )
                        dateAndTime = dateFormatter(logs[0]?.created_at ?? "", true, country)?.split(",")
                        date = dateAndTime[0]?.trim() ?? ""
                        time = dateAndTime[1]?.trim() ?? ""
                    }
                    return cell && cell?.length > 0 ? (
                        <>
                            <span className="d-block">{date}</span>
                            <small className="text-muted">{time}</small>
                        </>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Actions"),
                dataField: "actions",
                csvExport: false,
                hidden: createMAWBModel,
                formatter: (cell: number, row: Record<string, any>) => {
                    const actions = [
                        "Upload",
                        ...(!row.is_editable
                            ? (row.status_id ?? 0) > 0
                                ? ["Export Manifest"]
                                : ["Edit"]
                            : ["Export Manifest", "Edit"]),
                        ...(row.status_id === 38 || !row.no_of_pallets ? [] : ["Update Status"])
                    ]
                    return (
                        <Dropdown className="actions-menu">
                            <Dropdown.Toggle id="dropdown-basic" size={"sm"} className="py-0 secondary-bg">
                                {t("Actions")}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {actions?.map((action: string) => (
                                    <Dropdown.Item
                                        key={action}
                                        className="font-13"
                                        onClick={() => handleAction(action, row)}>
                                        {action}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )
                }
            }
        ],
        Bags: [
            {
                text: t("Bag ID"),
                dataField: "bag_id",
                formatter: (cell: string, row: Record<string, any>) => (
                    <span className="highlighted-text">{cell}</span>
                )
            },
            {
                text: t("No. of parcels"),
                dataField: "parcels_count",
                formatter: (cell: any, row: Record<string, any>) => {
                    return cell ?? row.parcels?.length ?? 0
                }
            },
            {
                text: t("Type"),
                dataField: "bag_type",
                formatter: (cell, row) =>
                    (row.bag_type ?? "").toLowerCase() === "transit" ? (
                        <div className="d-flex flex-row flex-nowrap align-items-center">
                            {cell}
                            <Flag
                                className="d-inline-block mx-1"
                                style={{ width: 28, height: 18 }}
                                code={row.final_destination ?? ""}
                            />
                            {row.final_destination}
                        </div>
                    ) : (
                        (cell ?? "-")
                    )
            },
            {
                text: t("Clearance Agent Details"),
                dataField: "agent_detail",
                formatter: (cell) => cell ?? "-"
            },
            {
                text: t("Dimensions"),
                dataField: "dims",
                formatter: (cell: any, row: Record<string, any>) =>
                    `${
                        row?.length || "-"
                    } x ${row?.width || "-"} x ${row?.height || "-"} ${row?.dimensions_unit || "inch|cm"}`
            },
            {
                text: t("A. Weight"),
                dataField: "a-weight",
                formatter: (cell: any, row: Record<string, any>) => {
                    return `${row?.actual_weight_value || "-"} ${row?.weight_unit || "lb|kg"}`
                }
            },
            {
                text: t("Destination"),
                dataField: "destination",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id={`lh-${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>
                            }>
                            <>
                                <Flag
                                    className="d-inline-block mx-1"
                                    style={{ width: 28, height: 18 }}
                                    code={(cell ?? "").toLowerCase()}
                                />
                                &nbsp;{cell}
                            </>
                        </OverlayTrigger>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Value"),
                dataField: "bag_invoice_value",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell && cell > 0 ? `$${cell}` : "-"
                }
            },
            {
                text: t("Actions"),
                dataField: "actions",
                csvExport: false,
                hidden: createMAWBModel,
                formatter: (cell: number, row: Record<string, any>) => {
                    let actions = ["Print Label", "Edit", "Delete"]
                    return (
                        <Dropdown className="actions-menu">
                            <Dropdown.Toggle id="dropdown-basic" size={"sm"} className="py-0 secondary-bg">
                                {t("Actions")}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {actions?.map((action: string) => (
                                    <Dropdown.Item
                                        key={action}
                                        className="font-13"
                                        onClick={() => handleAction(action, row)}>
                                        {action}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )
                }
            }
        ]
    }

    const onSelect = (row: Record<string, any>, isSelect: any) => {
        if (isSelect) {
            setSelectedRows([...selectedRows?.filter((sp) => sp !== row?.pallet_id), row?.pallet_id])
        } else {
            setSelectedRows(selectedRows?.filter((sp) => sp !== row?.pallet_id))
        }
    }

    const handleSelectAll = (isSelect: any, rows: Record<string, any>[]) => {
        if (isSelect) {
            setSelectedRows(rows?.map((sp) => sp?.pallet_id))
        } else {
            setSelectedRows([])
        }
    }

    const selectRow: any = {
        mode: "checkbox",
        clickToSelect: true,
        onSelect: onSelect,
        onSelectAll: handleSelectAll,
        selectColumnPosition: "left",
        hideSelectAll: true,
        selected: selectedRows
    }

    const { ExportCSVButton } = CSVExport

    const expandRow = React.useMemo(
        () => ({
            renderer: (row: any) =>
                tableName === "Pallets" ? (
                    <LineHaulPalletsTableExpandedRow pallet={row} />
                ) : tableName === "MAWBs" ? (
                    <LineHaulMAWBSTableExpandedRow
                        mawb={row}
                        showPalletDetails={showPalletDetails}
                        deleteDocument={deleteDocument}
                    />
                ) : tableName === "Bags" ? (
                    <LineHaulBagsTableExpandedRow bag={row} />
                ) : null,
            showExpandColumn: true,
            expandByColumnOnly: true,
            expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
                return <></>
            },
            expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
                if (expanded) {
                    return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
                }
                return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            },
            onlyOneExpanding: true
        }),
        [tableName]
    )

    const handleFilterChange: any = (e: any) => {
        const updatedOptions = { ...options }
        updatedOptions[e.target.name][tableId] = e.target.value ?? ""
        setOptions(updatedOptions)
    }

    const handleFilterFormSubmit = (e: any) => {
        e.preventDefault()
        const _options = { ...options }
        const page = { ..._options.page }
        page[tableId] = 1
        _options.page = page
        setExportParams({
            keyword: _options.keyword[tableId],
            from: _options.from[tableId],
            to: _options.to[tableId],
            status: _options.status[tableId]
        })
        setOptions(_options)
    }

    const handleExport = async () => {
        try {
            const response = await dispatch(
                exportList({
                    ...exportParams,
                    warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                    key: tableName === "MAWBs" ? "lhMawbs" : tableName === "Pallets" ? "lhPallets" : "lhBags"
                })
            ).unwrap()
            const data = response.data ?? ""
            if (!data?.trim?.()) throw new Error("No data found")
            const outputFilename = `${Date.now()}.csv`
            const aElement = document.createElement("a")
            aElement.setAttribute("download", outputFilename)
            const href = URL.createObjectURL(new Blob([data]))
            aElement.href = href
            aElement.setAttribute("target", "_blank")
            aElement.click()
            URL.revokeObjectURL(href)
            showMessage(`Data are exported successfully`, "success", "Success")
        } catch (error: any) {
            showMessage(`Data export error, reason: ${error.message}`, "danger", "Error")
        }
    }

    const keyFields: Record<string, string> = {
        Pallets: "pallet_id",
        MAWBs: "mawb",
        Bags: "id"
    }

    return (
        <div className="table-responsive">
            {columns[tableName] && (
                <ToolkitProvider
                    keyField={keyFields[tableName]}
                    data={data}
                    columns={columns[tableName]}
                    search={{ searchFormatted: true }}>
                    {(props) => (
                        <div className="table-container my-4">
                            {!createMAWBModel ? (
                                <>
                                    <form
                                        id={`searchForm-${tableName}`}
                                        className="hidden"
                                        onSubmit={handleFilterFormSubmit}
                                    />
                                    <Row className="gx-1 mx-2 pt-1">
                                        <Col className="mb-2" xs={6} lg>
                                            <Form.Control
                                                form={`searchForm-${tableName}`}
                                                className="h-100"
                                                placeholder="#"
                                                name="keyword"
                                                value={options.keyword[tableId]}
                                                onChange={handleFilterChange}
                                                disabled={loading || exporting || statusesLoading}
                                            />
                                        </Col>
                                        {tableId === "mawbs" ? (
                                            <Col className="mb-2" xs={6} lg>
                                                <Form.Select
                                                    form={`searchForm-${tableName}`}
                                                    className="h-100"
                                                    placeholder="Status"
                                                    name="status"
                                                    value={options.status[tableId]}
                                                    onChange={handleFilterChange}
                                                    disabled={loading || exporting || statusesLoading}>
                                                    <option value="">Select status</option>
                                                    {statuses.map((status: any) => (
                                                        <option value={status.id} key={status.id}>
                                                            {status?.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                        ) : null}
                                        <Col className="mb-2" xs={6} lg>
                                            <Form.Control
                                                form={`searchForm-${tableName}`}
                                                className="h-100"
                                                type="date"
                                                placeholder="From"
                                                name="from"
                                                value={options.from[tableId]}
                                                onChange={handleFilterChange}
                                                max={options.to[tableId]}
                                                disabled={loading || exporting || statusesLoading}
                                            />
                                        </Col>
                                        <Col className="mb-2" xs={6} lg>
                                            <Form.Control
                                                form={`searchForm-${tableName}`}
                                                className="h-100"
                                                type="date"
                                                placeholder="To"
                                                name="to"
                                                value={options.to[tableId]}
                                                onChange={handleFilterChange}
                                                min={options.from[tableId]}
                                                disabled={loading || exporting || statusesLoading}
                                            />
                                        </Col>
                                        <Col className="mb-2" xs={createMAWBModel ? 6 : 4} lg>
                                            <Button
                                                className="h-100 w-100"
                                                variant="primary"
                                                type="submit"
                                                form={`searchForm-${tableName}`}
                                                disabled={loading || exporting || statusesLoading}>
                                                Search
                                            </Button>
                                        </Col>
                                        {createMAWBModel ? null : (
                                            <Col className="mb-2" xs={4} lg>
                                                <Button
                                                    type="button"
                                                    className="secondary-bg no-wrap w-100"
                                                    onClick={() => {
                                                        handleAction("Create", {})
                                                    }}>
                                                    {`Create ${
                                                        tableName === "Pallets"
                                                            ? "Pallet"
                                                            : tableName === "MAWBs"
                                                              ? "MAWB"
                                                              : "Bag"
                                                    }`}
                                                </Button>
                                            </Col>
                                        )}
                                        {tableName === "MAWBs" ? (
                                            <Col className="mb-2" xs={createMAWBModel ? 6 : 4} lg>
                                                <Button
                                                    className="h-100 w-100"
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={handleExport}
                                                    disabled={loading || exporting || statusesLoading}>
                                                    Export
                                                </Button>
                                            </Col>
                                        ) : null}
                                    </Row>
                                </>
                            ) : null}

                            <Row className="table-data my-3">
                                <Col className="mx-2">
                                    <div
                                        style={
                                            createMAWBModel
                                                ? {
                                                      maxHeight: "400px",
                                                      overflow: "auto"
                                                  }
                                                : {}
                                        }>
                                        <RemotePagination
                                            data={data}
                                            page={
                                                tableName !== "Pallets"
                                                    ? options?.page?.mawbs
                                                    : options?.page?.pallets
                                            }
                                            sizePerPage={options?.sizePerPage}
                                            totalSize={
                                                tableName !== "Pallets"
                                                    ? options?.totalSize?.mawbs
                                                    : options?.totalSize?.pallets
                                            }
                                            onTableChange={handleTableChange}
                                            columns={columns[tableName]}
                                            keyField={tableName === "Pallets" ? "pallet_id" : "mawb"}
                                            expandRow={createMAWBModel ? {} : expandRow}
                                            props={props}
                                            selectRow={createMAWBModel ? selectRow : null}
                                            remote={createMAWBModel ? false : true}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            {/* <Row className="mt-4 mx-1">
									<Col xs={12} sm={12} md={12} lg={12} className="text-align-end ">
										<Button variant="primary" type="submit" className="w-15" onClick={() => printMenifest()}>
											{t("Print_Manifest")}
										</Button>
									</Col>
								</Row> */}
                            {manifestData?.mawb && (
                                <div className="d-none">
                                    <ExportManifest
                                        manifestData={manifestData}
                                        setManifestData={setManifestData}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default LineHaulTable
